import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { FileMinus2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { AccionesDelFormulario, Campo, Modal, claseEntrada } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import { cajasQueAcreditan } from './cajas';

interface Motivo {
  id: number;
  codigo: string;
  nombre: string;
}

interface Punto {
  id: number;
  codigo: string;
  nombre: string;
  activo: boolean;
  usuarioNombre: string | null;
  comprobantes?: { tipo: string; habilitado: boolean }[];
  deNotas?: boolean;
}

export function DialogoDeNotaDeCredito({
  abierto,
  factura,
  alCerrar,
  alEmitir,
}: {
  abierto: boolean;

  factura: { id: number; numero: string | null; cliente: string; total: number } | null;
  alCerrar: () => void;
  alEmitir: () => void;
}) {
  const [motivoId, setMotivoId] = useState('');
  const [tipo, setTipo] = useState<'total' | 'parcial'>('total');
  const [importe, setImporte] = useState('');
  const [observacion, setObservacion] = useState('');
  const [aprobadoPor, setAprobadoPor] = useState('');
  const [punto, setPunto] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const motivos = useQuery({
    queryKey: ['motivos-de-correccion'],
    queryFn: () => api.get<Motivo[]>('/motivos-de-correccion'),
    enabled: abierto,
  });

  const puntos = useQuery({
    queryKey: ['puntos-emision'],
    queryFn: () => api.get<Punto[]>('/puntos-emision'),
    enabled: abierto,
  });

  const cajas = cajasQueAcreditan(puntos.data ?? []);

  if (factura === null) {
    return null;
  }

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (factura === null) {
      return;
    }

    setError(null);
    setEnviando(true);
    try {
      const resultado = await api.post<{ numero: string; situacion: string; mensaje: string }>(
        `/notas-credito?puntoEmision=${punto}`,
        {
          facturaId: factura.id,
          motivoId: Number(motivoId),
          tipo,
          importe: tipo === 'parcial' ? importe : null,
          observacion,
          aprobadoPor,
        },
      );

      if (resultado.situacion === 'autorizada') {
        notificar.exito(`${resultado.numero} · AUTORIZADA POR EL SRI`);
      } else {
        notificar.error(`${resultado.numero} · ${resultado.mensaje}`);
      }

      alEmitir();
      alCerrar();
    } catch (causa) {
      const mensaje =
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo emitir la nota de crédito';
      setError(mensaje);
      notificar.error(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      abierto={abierto}
      alCerrar={alCerrar}
      titulo="Acreditar factura"
      subtitulo={`${factura.numero} · ${factura.cliente}`}
      icono={FileMinus2}
      cerrarAlPulsarFuera={false}
    >
      <form onSubmit={(evento) => void enviar(evento)} className="space-y-4">
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
          >
            {error}
          </p>
        )}

        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] uppercase leading-relaxed text-amber-900">
          Se emite una nota de crédito ante el SRI. Cuando la autorice, esta factura de $
          {factura.total.toFixed(2)} queda sin efecto. No se deshace: el motivo viaja dentro del
          comprobante.
        </p>

        <Campo etiqueta="Qué se acredita" requerido>
          <span className="flex gap-1">
            {(['total', 'parcial'] as const).map((valor) => (
              <button
                key={valor}
                type="button"
                onClick={() => setTipo(valor)}
                aria-pressed={tipo === valor}
                className={`h-9 flex-1 rounded-lg border text-[11px] font-semibold uppercase transition ${
                  tipo === valor
                    ? 'border-marca-400 bg-marca-50 text-marca-700'
                    : 'border-armazon-200 bg-white text-armazon-500 hover:border-armazon-300'
                }`}
              >
                {valor === 'total' ? `Todo · $${factura.total.toFixed(2)}` : 'Una parte'}
              </button>
            ))}
          </span>
        </Campo>

        {tipo === 'parcial' && (
          <Campo
            etiqueta="Importe a acreditar"
            requerido
            ayuda="NO PUEDE PASAR DEL TOTAL DE LA FACTURA, SUMANDO LO YA ACREDITADO"
          >
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={factura.total}
              value={importe}
              onChange={(evento) => setImporte(evento.target.value)}
              required
              className={claseEntrada}
            />
          </Campo>
        )}

        <Campo etiqueta="Motivo" requerido ayuda="VA DENTRO DEL COMPROBANTE, ANTE EL SRI">
          <select
            value={motivoId}
            onChange={(evento) => setMotivoId(evento.target.value)}
            required
            autoFocus
            className={claseEntrada}
          >
            <option value="">Elija el motivo</option>
            {(motivos.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.nombre}
              </option>
            ))}
          </select>
        </Campo>

        <Campo etiqueta="Por qué caja sale" requerido ayuda="LA SERIE VA IMPRESA EN LA NOTA">
          <select
            value={punto}
            onChange={(evento) => setPunto(evento.target.value)}
            required
            className={claseEntrada}
          >
            <option value="">Elija la caja</option>
            {cajas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.codigo} · {p.nombre}
              </option>
            ))}
          </select>
        </Campo>

        <Campo etiqueta="Quién lo autoriza" ayuda="QUEDA EN EL REGISTRO, NO EN EL COMPROBANTE">
          <input
            value={aprobadoPor}
            onChange={(evento) => setAprobadoPor(evento.target.value)}
            maxLength={120}
            className={claseEntrada}
            placeholder="Nombre o cargo de quien lo aprueba"
          />
        </Campo>

        <Campo etiqueta="Observación" ayuda="EL DETALLE QUE NO CABE EN EL MOTIVO DEL CATÁLOGO">
          <textarea
            value={observacion}
            onChange={(evento) => setObservacion(evento.target.value)}
            rows={2}
            maxLength={500}
            className={`${claseEntrada} resize-none`}
            placeholder="Lo que haga falta para entender el caso dentro de un año"
          />
        </Campo>

        <AccionesDelFormulario
          alCancelar={alCerrar}
          enviando={enviando}
          textoEnviar="Emitir la nota de crédito"
        />
      </form>
    </Modal>
  );
}
